import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tasksApi, taskStatusesApi } from '../services/api';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTask } from '../contexts/TaskContext';
import {
  Plus, MoreHorizontal, Clock, Calendar, CalendarClock,
  CheckCircle2, Circle, GripVertical, Paperclip, Tag
} from 'lucide-react';
import { PRIORITIES, extractPlainText } from '../lib/constants';
import { statusName, statusValueName } from '../lib/statusName';
import UnreadBadge from './unread/UnreadBadge';
import { useUnreadsQuery } from '../hooks/queries';
import { SpotlightMark, useSpotlightMarks } from './spotlight/SpotlightMenu';
import { getDefaultFields } from '../lib/viewFields';
import InlineTaskRow from './InlineTaskRow';
import ProjectChip from './ProjectChip';
import AssigneePicker from './task/AssigneePicker';

// Legacy task.status enum → custom-status category. Used to place a task whose
// status_id is missing/unknown into the right column by its category instead of
// blindly dumping it into the first (To Do) column.
const LEGACY_STATUS_TO_CATEGORY = {
  todo: 'not_started', in_progress: 'in_progress', completed: 'completed', archived: 'archived',
};
const KB_STATUS_COLORS = {
  todo: 'bg-gray-500/15 text-muted-foreground',
  in_progress: 'bg-blue-500/15 text-blue-400',
  completed: 'bg-green-500/15 text-green-500',
  archived: 'bg-zinc-500/15 text-zinc-400',
};

function kbRenderCustom(def, raw, lang) {
  if (raw == null || raw === '') return null;
  if (def.field_type === 'checkbox') return raw ? '✓' : '-';
  if (def.field_type === 'multi_select' && Array.isArray(raw)) {
    return raw.map(id => def.options?.find(o => o.id === id)?.label).filter(Boolean).join(', ') || null;
  }
  if (def.field_type === 'select') return def.options?.find(o => o.id === raw)?.label || null;
  if (def.field_type === 'date') { try { return new Date(raw).toLocaleDateString(lang); } catch { return String(raw); } }
  return String(raw);
}

// Backward-compat alias: KanbanBoard uses .color for bgColor
const priorityConfig = Object.fromEntries(
  Object.entries(PRIORITIES).map(([k, v]) => [k, { ...v, color: v.bgColor, label: v.altLabel }])
);

const KanbanBoard = ({ tasks = [], projects = [], onUpdate, prefillProjectId = null, teamId = null, visibleFields = null, customFieldDefs = [], readOnly = false, statusConfigs: statusConfigsProp = null }) => {
  const fields = useMemo(() => {
    if (Array.isArray(visibleFields)) return new Set(visibleFields);
    return new Set([...getDefaultFields('board'), ...customFieldDefs.map(d => `cf_${d.id}`)]);
  }, [visibleFields, customFieldDefs]);
  const show = (key) => fields.has(key);
  const { t, i18n } = useTranslation();

  // One hook call for the whole board: cards render inside a .map(), so they
  // read from this shared snapshot rather than each calling a hook.
  const { data: unreads } = useUnreadsQuery();
  const hasUnread = (id) => Boolean(unreads?.tasks?.[id]);
  const spotlight = useSpotlightMarks();  // marks on cards: in Spotlight, or put it there
  const { formatDate } = useDateTime();

  // Local tasks copy for optimistic updates
  const [localTasks, setLocalTasks] = useState(tasks);

  // Sync localTasks when parent tasks prop changes
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Status configs state. Public/read-only callers can supply the configs via
  // the `statusConfigs` prop so we skip the auth-required API call entirely.
  const [statusConfigs, setStatusConfigs] = useState(
    Array.isArray(statusConfigsProp)
      ? statusConfigsProp.filter(s => s.category !== 'archived')
      : [],
  );
  const [loading, setLoading] = useState(!Array.isArray(statusConfigsProp));

  // TaskContext
  const { openTaskCreation, openTaskDetail } = useTask();

  // Drag state
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Load status configurations from the API only when not provided externally.
  useEffect(() => {
    if (Array.isArray(statusConfigsProp)) {
      setStatusConfigs(statusConfigsProp.filter(s => s.category !== 'archived'));
      setLoading(false);
      return;
    }
    loadStatusConfigs();
    // eslint-disable-next-line
  }, [teamId, statusConfigsProp]);

  const loadStatusConfigs = async () => {
    try {
      const statuses = await taskStatusesApi.getAll(teamId);
      // Filter out archived statuses from Kanban view
      const activeStatuses = statuses.filter(s => s.category !== 'archived');
      setStatusConfigs(activeStatuses);
    } catch (err) {
      console.error('Error loading status configs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group tasks by status_id
  const tasksByStatus = useMemo(() => {
    const grouped = {};

    // Initialize empty arrays for each status
    statusConfigs.forEach(status => {
      grouped[status.id] = [];
    });

    // Group tasks
    localTasks.forEach(task => {
      const statusId = task.status_id;
      if (statusId && grouped[statusId]) {
        grouped[statusId].push(task);
      } else if (statusConfigs[0]) {
        // status_id missing or not a column on this board → place by the task's
        // legacy status category (e.g. a completed task lands in the Done
        // column) rather than always falling into the first column, which made
        // Completed tasks show up under To Do.
        const category = LEGACY_STATUS_TO_CATEGORY[task.status] || 'not_started';
        const target = statusConfigs.find(s => s.category === category) || statusConfigs[0];
        grouped[target.id].push(task);
      }
    });

    // Sort by priority within each column
    const priorityOrder = { p1: 1, p2: 2, p3: 3, p4: 4 };
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4));
    });

    return grouped;
  }, [localTasks, statusConfigs]);

  const getProjectById = (projectId) => projects.find(p => p.id === projectId);

  const handleTaskClick = (task) => {
    if (readOnly) return;
    openTaskDetail(task.id, { teamId });
  };

  const handleAddTask = (statusId) => {
    if (readOnly) return;
    openTaskCreation({ statusId, projectId: prefillProjectId, teamId });
  };

  const handleDragStart = (e, task) => {
    if (readOnly) { e.preventDefault(); return; }
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
    setTimeout(() => {
      e.target.classList.add('opacity-50');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('opacity-50');
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(statusId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, newStatusId) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedTask) return;

    const previousStatusId = draggedTask.status_id;
    setDraggedTask(null);

    if (previousStatusId === newStatusId) return;

    // Optimistic update - move card immediately
    setLocalTasks(prev => prev.map(t =>
      t.id === draggedTask.id ? { ...t, status_id: newStatusId } : t
    ));

    try {
      await tasksApi.update(draggedTask.id, { status_id: newStatusId });
      onUpdate?.();
    } catch (err) {
      console.error('Error updating task status:', err);
      // Revert on failure
      setLocalTasks(prev => prev.map(t =>
        t.id === draggedTask.id ? { ...t, status_id: previousStatusId } : t
      ));
    }
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date < today) return { text: t('tasks.overdue'), className: 'text-red-400' };
    if (date < tomorrow) return { text: t('common.today'), className: 'text-amber-700 dark:text-amber-400' };
    if (date < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) return { text: t('common.tomorrow'), className: 'text-blue-400' };

    return { text: formatDate(date, false), className: 'text-muted-foreground' };
  };

  // Get status config for a task to determine if it's completed. Mirror the
  // grouping fallback: if status_id doesn't match a column, resolve by the
  // task's legacy status category so a completed card shows completed styling.
  const getTaskStatusConfig = (task) => {
    const byId = statusConfigs.find(s => s.id === task.status_id);
    if (byId) return byId;
    const category = LEGACY_STATUS_TO_CATEGORY[task.status] || 'not_started';
    return statusConfigs.find(s => s.category === category);
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-12rem)] flex items-center justify-center">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh-15rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-[calc(100vh-12rem)] flex gap-4 overflow-x-auto pb-4">
      {statusConfigs.map((statusConfig) => {
        const columnTasks = tasksByStatus[statusConfig.id] || [];
        const isDropTarget = dragOverColumn === statusConfig.id && draggedTask?.status_id !== statusConfig.id;

        return (
          <div
            key={statusConfig.id}
            className="flex-shrink-0 w-80 flex flex-col"
            onDragOver={(e) => handleDragOver(e, statusConfig.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, statusConfig.id)}
          >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: statusConfig.color }}>
                  {statusName(statusConfig, t)}
                </span>
                <span
                  className="px-2 py-0.5 rounded-full text-xs"
                  style={{
                    backgroundColor: statusConfig.color + '33',
                    color: statusConfig.color
                  }}
                >
                  {columnTasks.length}
                </span>
              </div>
              {!readOnly && (
                <button
                  onClick={() => handleAddTask(statusConfig.id)}
                  className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Column Content */}
            <div
              className={`flex-1 rounded-xl border-2 border-dashed p-2 space-y-2 overflow-y-auto transition-colors ${
                isDropTarget
                  ? 'border-amber-500/50 bg-amber-500/5'
                  : 'border-transparent bg-accent/60'
              }`}
            >
              {columnTasks.map((task) => {
                const priority = priorityConfig[task.priority] || priorityConfig.p4;
                const project = getProjectById(task.project_id);
                const dueInfo = formatDueDate(task.due_date);
                const taskStatus = getTaskStatusConfig(task);
                const isCompleted = taskStatus?.category === 'completed';

                return (
                  <div
                    key={task.id}
                    draggable={!readOnly}
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleTaskClick(task)}
                    className={`bg-popover rounded-lg border-l-4 p-3 ${readOnly ? '' : 'cursor-pointer hover:bg-accent'} transition-colors group ${priority.borderColor}`}
                  >
                    {/* Drag Handle & Task Title */}
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 text-muted-foreground/70 opacity-0 group-hover:opacity-100 flex-shrink-0 cursor-grab" />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm flex items-center gap-1.5 ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'} ${hasUnread(task.id) ? 'font-semibold' : 'font-medium'}`}>
                          <span className="min-w-0 break-words">{task.title}</span>
                          <UnreadBadge taskId={task.id} className="flex-shrink-0" />
                          <SpotlightMark taskId={task.id} mark={spotlight.items[task.id]} sections={spotlight.sections} addable={!readOnly} />
                        </div>

                        {/* Description preview */}
                        {show('description') && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {extractPlainText(task.description) || <span className="text-muted-foreground/40">-</span>}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Task Meta - once a field is toggled on, always render
                        a chip so the user sees the change immediately. */}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {show('status') && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${KB_STATUS_COLORS[task.status] || KB_STATUS_COLORS.todo}`}>
                          {!task.status || LEGACY_STATUS_TO_CATEGORY[task.status] ? statusValueName(task.status || 'todo', t) : task.status}
                        </span>
                      )}
                      {show('priority') && (
                        <span className={`px-1.5 py-0.5 rounded text-xs ${priority.textColor} bg-accent/60`}>
                          {t(priority.altLabelKey)}
                        </span>
                      )}

                      {show('start_date') && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarClock className="w-3 h-3" />
                          {task.start_date ? formatDate(new Date(task.start_date), false) : '-'}
                        </span>
                      )}

                      {show('due_date') && (
                        <span className={`flex items-center gap-1 text-xs ${dueInfo?.className || 'text-muted-foreground/50'}`}>
                          <Calendar className="w-3 h-3" />
                          {dueInfo?.text || '-'}
                        </span>
                      )}

                      {show('duration') && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {task.duration ? t('ui_task.duration.minutes', { m: task.duration }) : '-'}
                        </span>
                      )}

                      {show('labels') && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Tag className="w-3 h-3" />
                          {task.label_ids?.length || 0}
                        </span>
                      )}

                      {show('attachments') && (
                        <span className="flex items-center gap-0.5 text-xs text-muted-foreground/70">
                          <Paperclip className="w-3 h-3" />
                          {task.attachments?.length || 0}
                        </span>
                      )}

                      {customFieldDefs.map(def => {
                        if (!fields.has(`cf_${def.id}`)) return null;
                        const val = kbRenderCustom(def, task.custom_fields?.[def.id], i18n.language);
                        return (
                          <span key={def.id} className="text-xs text-muted-foreground/70 flex items-center gap-1">
                            <span className="text-muted-foreground/70">{def.name}:</span>
                            <span className="truncate max-w-[100px]">{val ?? '-'}</span>
                          </span>
                        );
                      })}

                      {/* Who works on it, people and the bot: shown when set, a click away when not. */}
                      {!readOnly && (
                        <AssigneePicker task={task} project={project} alwaysVisible={show('assignee')}
                          onChange={(patch) => setLocalTasks((prev) => prev.map((x) => (x.id === task.id ? { ...x, ...patch } : x)))} />
                      )}
                    </div>

                    {/* Project */}
                    {show('project') && (
                      <div className="flex items-center justify-between mt-2">
                        {project
                          ? <ProjectChip project={project} textClass="text-xs text-muted-foreground" />
                          : <span className="text-xs text-muted-foreground/50">-</span>}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Empty State */}
              {columnTasks.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">{t('tasks.no_tasks')}</p>
                </div>
              )}

              {/* Inline quick-add */}
              <InlineTaskRow
                projectId={prefillProjectId}
                statusId={statusConfig.id}
                teamId={teamId}
                onTaskCreated={() => onUpdate?.()}
                placeholder={t('tasks.task_title_placeholder')}
                className="rounded-lg"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanBoard;
